import React from 'react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { MagnifyingGlassIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@frontend/shadcn/components/ui/button';

const { useState, useMemo } = React;

interface FieldSortDropdownProps {
  fields: { displayName: string, id: string }[];
  onSelect: (field: string, direction: 'asc' | 'desc') => void;
  triggerLabel?: string | React.JSX.Element;
}

const FieldSortDropdown: React.FC<FieldSortDropdownProps> = ({
  fields,
  onSelect,
  triggerLabel = 'Sort By',
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter fields by search term (case insensitive)
  const filteredFields = useMemo(() => {
    const lowerTerm = searchTerm.toLowerCase();
    return fields.filter((field) => field.displayName.toLowerCase().includes(lowerTerm));
  }, [fields, searchTerm]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">{triggerLabel}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <div className="px-2 py-1.5">
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search for field"
              className="w-full px-2 py-1 text-sm outline-none bg-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label="Search for field to sort by"
              autoComplete="off"
            />
          </div>
          <div className="h-2 border-b border-gray-200" />
        </div>
        <div className="max-h-80 overflow-y-auto">
          {/* Filtered field items */}
          {filteredFields.length > 0 ? (
            filteredFields.map((field) => (
              <DropdownMenuSub key={field.id}>
                <DropdownMenuSubTrigger>
                  {field.displayName}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-40">
                  <DropdownMenuItem onSelect={() => onSelect(field.id, 'asc')}>
                    Ascending
                    <ArrowUpIcon className="ml-auto h-4 w-4" />
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onSelect(field.id, 'desc')}>
                    Descending
                    <ArrowDownIcon className="ml-auto h-4 w-4" />
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No fields found
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FieldSortDropdown;
